<!-- 
  Log Form Component
  ------------------
  This component is the main log form for the Receiving Manager Web App. It provides a comprehensive UI for users to input 
  and manage log entries, including various modals for additional information and contact management.

  Features:
  - Responsive design: Adjusts layout based on screen size.
  - Main form includes fields for PO number, job name, job number, vendor, quantity units, job type, JC department, shelf location, 
    notify list, items, notes, and print label options.
  - Modals for additional information:
    - Location Information Modal: Displays delivery location and technician team details.
    - Add/Update Contacts Modal: Allows users to manage contacts associated with the PO.
  - Conditional rendering of form elements based on user input, PO data, and screen size.
  - Print functionality: Users can specify print quantities and types, and print labels directly from the form.
  - Email functionality: Users can generate an email with log details.
  - Submit button to finalize and submit the log entry.

-->

<!--Modal for Location Information-->
<ion-modal
  class="info-blurb-popup"
  ion-modal
  [isOpen]="isLocaitonInfoModalOpen"
  (didDismiss)="onCloseLocationInfo()"
>
  <ng-template>
    <!-- Modal Header: Toolbar with title and close button -->
    <ion-toolbar class="location-info-toolbar">
      <ion-title>Provided Location Info</ion-title>
      <ion-buttons slot="end">
        <ion-button (click)="onCloseLocationInfo()">
          <!-- Close button -->
          <ion-icon name="close" slot="start"></ion-icon>
          Close
        </ion-button>
      </ion-buttons>
    </ion-toolbar>

    <!-- Modal Content -->
    <ion-content>
      <!-- List of location information -->
      <ion-list>
        <!-- Delivery Location Section -->

        <ion-item-divider>
          <ion-label>Delivery Location</ion-label>
        </ion-item-divider>
        <ion-item>
          <span *ngIf="locationInfo.deliveryLocation">
            {{ locationInfo.deliveryLocation }}</span
          ><span *ngIf="!locationInfo.deliveryLocation">Not Provided</span>
        </ion-item>

        <!-- Technician Team Section -->
        <ion-item-divider>
          <ion-label>Technician Team </ion-label>
        </ion-item-divider>
        <ion-item>
          <span *ngIf="locationInfo.technicianTeam">
            {{ locationInfo.technicianTeam }}</span
          ><span *ngIf="!locationInfo.technicianTeam">Not Provided</span>
        </ion-item>
      </ion-list>
    </ion-content>
  </ng-template>
</ion-modal>

<!-- Modal for Adding/Updating Contacts -->
<ion-modal
  class="card-popup"
  [isOpen]="isAddContactModalOpen"
  [backdropDismiss]="true"
  (didDismiss)="closeAddContactModal()"
>
  <ng-template>
    <!-- Modal Header: Toolbar with title and close button -->
    <ion-header>
      <ion-toolbar>
        <ion-title>Edit Contacts</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="closeAddContactModal()">
            <ion-icon name="close" slot="start"></ion-icon>
            Close
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <!-- Modal Content -->
    <ion-content>
      <!-- Add Contact Component -->
      <app-add-contact
        [primaryContact]="primaryContact"
        [allContactInfo]="allPoContactData"
        [notifyList]="notifyList"
        [employeeEmails]="employeeEmails"
        (updateNotifyList)="onUpdateNotifyList($event)"
        (updatePrimaryContact)="onUpdatePrimaryContact($event)"
      ></app-add-contact>
    </ion-content>
  </ng-template>
</ion-modal>

<!-- Modal for Photo Management -->
<ion-modal
  class="card-popup"
  [isOpen]="isPhotoManagerModalOpen"
  [backdropDismiss]="true"
  (didDismiss)="onClosePhotoManager()"
>
  <ng-template>
    <!-- Modal Header: Toolbar with title and close button -->
    <ion-header>
      <ion-toolbar>
        <ion-title>Log Photo Manager</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="onClosePhotoManager()">
            <ion-icon name="close" slot="start"></ion-icon>
            Close
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <!-- Modal Content -->
    <ion-content>
      <!-- Add Contact Component -->
      <app-photo-manager
        [photosDisabled]="photosDisabled"
        (photoInclusionChanged)="onTogglePhotoInclusion($event)"
      ></app-photo-manager>
    </ion-content>
  </ng-template>
</ion-modal>

<!-- Email Preview Modal -->
<app-preview-email-modal
  [isOpen]="isEmailPreviewModalOpen"
  [includePhotosInEmail]="includePhotosInEmail"
  (togglePhotosInEmail)="handleTogglePhotosInEmail($event)"
  (closeModal)="onCloseEmailPreviewModal()"
  (sendEmail)="onSendEmail()"
>
</app-preview-email-modal>

<!-- ============================================================ -->
<!-- PRINT CONFIG MODAL                                           -->
<!-- Opened from the Print button. Shows the print defaults and    -->
<!-- lets the user tweak type / qty / method; in Auto mode the     -->
<!-- live printer list populates the printer pickers.              -->
<!-- ============================================================ -->
<ion-modal
  class="card-popup"
  [isOpen]="isPrintConfigModalOpen"
  [backdropDismiss]="true"
  (didDismiss)="onClosePrintConfig()"
>
  <ng-template>
    <ion-header>
      <ion-toolbar>
        <ion-title>Print Settings</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="onClosePrintConfig()">
            <ion-icon name="close" slot="start"></ion-icon>
            Close
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="print-config-content">
      <ion-list>
        <!-- What to print -->
        <ion-item lines="none">
          <ion-select
            label="What to print"
            [labelPlacement]="labelPlacement"
            interface="popover"
            [formControl]="printTypeControl"
          >
            <ion-select-option
              *ngFor="let option of printTypeOptions"
              [value]="option.label"
            >
              {{ option.label }}
            </ion-select-option>
          </ion-select>
        </ion-item>

        <!-- No print-method choice here, and there is none to make: where each
             document goes is decided by the facility's printers and the outage
             switch, not by this form. See print-path.rules.ts. -->

        <!-- Quantities (per selected type) -->
        <ion-item lines="none" *ngIf="showDymoPrintQty">
          <ion-input
            type="number"
            min="1"
            label="Label qty"
            [labelPlacement]="labelPlacement"
            [formControl]="dymoQtyControl"
          ></ion-input>
        </ion-item>
        <ion-item lines="none" *ngIf="showFullSheetPrintQty">
          <ion-input
            type="number"
            min="1"
            label="Copier sheet qty"
            [labelPlacement]="labelPlacement"
            [formControl]="sheetQtyControl"
          ></ion-input>
        </ion-item>

        <!-- See docs/printing.md, "Browser rendering": state the
             per-container multiplication before printing, since a quantity of
             2 now means two copies of EACH container. -->
        <ion-item lines="none" *ngIf="printCountSummary">
          <ion-icon name="copy-outline" slot="start" color="medium"></ion-icon>
          <ion-label class="ion-text-wrap">
            <small>{{ printCountSummary }}</small>
          </ion-label>
        </ion-item>

        <!-- Decision 1: this print happens before the log is submitted, so
             there are no item GUIDs yet — every label from this button is the
             legacy no-QR document. See callPrintLabel()'s itemIds param. Shown
             only where the facility prints QR codes at all; elsewhere "no QR
             yet" would describe a thing that never happens here. -->
        <ion-item lines="none" *ngIf="browserQrAnyEnabled">
          <ion-icon name="information-circle-outline" slot="start" color="medium"></ion-icon>
          <ion-label class="ion-text-wrap">
            <small>This log has not been submitted yet, so whatever you print now — labels, copier sheets, or both — comes out without QR codes.</small>
          </ion-label>
        </ion-item>

        <!-- Task 3: browsers scale the page to fit by default, which shrinks
             the QR until it will not scan. Actual size is the only setting
             that prints the label at the calibrated 3in x 1in size. The
             scaling advice is worth giving either way — a shrunk label is a
             smaller label — but only the QR makes it urgent, so the reason
             stated changes with the facility. -->
        <ion-item lines="none" *ngIf="printsInBrowser">
          <ion-icon name="print-outline" slot="start" color="medium"></ion-icon>
          <ion-label class="ion-text-wrap">
            <small *ngIf="browserQrAnyEnabled"><strong>In the print dialog, set Scale to "Actual size" (100%)</strong> — anything smaller shrinks the QR code until it will not scan.</small>
            <small *ngIf="!browserQrAnyEnabled"><strong>In the print dialog, set Scale to "Actual size" (100%)</strong> — anything smaller prints undersized for the stock.</small>
          </ion-label>
        </ion-item>

        <!-- Named-printer selects, one per document actually going to the print
             API. A select for a document rendering in the browser would be a
             control with nothing behind it — the browser's own dialog owns that
             destination. Mirrors the reprint modal. -->
        <ion-item lines="none" *ngIf="printersLoading && (sendsZebraToBackend || sendsCopierToBackend)">
          <ion-spinner name="crescent" slot="start"></ion-spinner>
          <ion-label class="ion-text-wrap"><small>Loading printers…</small></ion-label>
        </ion-item>

        <ion-item lines="none" *ngIf="sendsZebraToBackend">
          <ion-select
            label="Label printer"
            [labelPlacement]="labelPlacement"
            interface="popover"
            [(ngModel)]="selectedZebraPrinter"
            [ngModelOptions]="{ standalone: true }"
            placeholder="Choose printer"
          >
            <ion-select-option
              *ngIf="selectedZebraPrinter && !printerNames.includes(selectedZebraPrinter)"
              [value]="selectedZebraPrinter"
            >
              {{ selectedZebraPrinter }} · configured
            </ion-select-option>
            <ion-select-option *ngFor="let p of availablePrinters" [value]="p.name">
              {{ p.name }}
            </ion-select-option>
          </ion-select>
        </ion-item>

        <ion-item lines="none" *ngIf="sendsCopierToBackend">
          <ion-select
            label="Copier printer"
            [labelPlacement]="labelPlacement"
            interface="popover"
            [(ngModel)]="selectedCopierPrinter"
            [ngModelOptions]="{ standalone: true }"
            placeholder="Choose printer"
          >
            <ion-select-option
              *ngIf="selectedCopierPrinter && !printerNames.includes(selectedCopierPrinter)"
              [value]="selectedCopierPrinter"
            >
              {{ selectedCopierPrinter }} · configured
            </ion-select-option>
            <ion-select-option *ngFor="let p of availablePrinters" [value]="p.name">
              {{ p.name }}
            </ion-select-option>
          </ion-select>
        </ion-item>

        <!-- Where the job actually comes out. Both lines can show at once: with
             the Zebra reported down at a facility that has both printers, the
             label renders here and the copier sheet still goes to the copier. -->
        <ion-item lines="none" *ngIf="printsInBrowser">
          <ion-icon name="information-circle-outline" slot="start" color="medium"></ion-icon>
          <ion-label class="ion-text-wrap">
            <small>Prints through your browser — choose the printer in the print dialog.</small>
          </ion-label>
        </ion-item>
      </ion-list>
    </ion-content>

    <ion-footer>
      <ion-toolbar>
        <ion-buttons slot="end">
          <ion-button (click)="onClosePrintConfig()">Cancel</ion-button>
          <ion-button
            color="lightBlue"
            fill="solid"
            (click)="onPrintFromConfig()"
          >
            <ion-icon name="print" slot="start"></ion-icon>
            Print
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-footer>
  </ng-template>
</ion-modal>

<!--Main page container-->
<div class="log-form-page-container">
  <!-- Display the form content based on screen size -->
  <div *ngIf="assetsDataLoaded" class="log-form-page-content">
    <ion-content *ngIf="isSmallScreen">
      <!--Log Form-->
      <ng-template [ngTemplateOutlet]="appform"> </ng-template>
    </ion-content>
    <ng-template *ngIf="!isSmallScreen" [ngTemplateOutlet]="appform">
    </ng-template>
  </div>
</div>

<!--Preview Log Modal-->
<app-preview-log-modal
  #previewLogModal
  [isOpen]="isPreviewLogModalOpen"
  (submitLog)="onSubmitLog()"
  (closeModal)="onPreviewLogModalClose()"
  [includePhotosInEmail]="includePhotosInEmail"
  [emailsDisabled]="emailsDisabled"
  [photosDisabled]="photosDisabled"
  [printingChecked]="printingChecked"
  [logFormData]="newLog"
  (togglePhotosInEmail)="handleTogglePhotosInEmail($event)"
  (toggleEmailsDisabled)="handleToggleEmailsDisabled($event)"
>
</app-preview-log-modal>

<!--Template for the View PO Button-->
<ng-template #logFormPOButton>
  <ion-buttons slot="end">
    <!-- Loading Spinner (Shown while data is loading) -->

    <div *ngIf="dataLoading.isLoading" class="spinner-container" slot="end">
      <ion-spinner name="dots"></ion-spinner> {{ dataLoading.message }}
    </div>

    <!-- View PO Button (Visible when not loading) -->
    <ion-button
      (click)="onViewPoReport()"
      *ngIf="!dataLoading.isLoading"
      title="Select SSRS Purchase Order"
    >
      <ion-icon name="document-text-outline"></ion-icon>
      View PO
    </ion-button>
  </ion-buttons>
</ng-template>

<!--Log Form section for print qty
This section is used to display the print quantity input fields and the print button within the log form
An ng-template is used for reuasbility as the print quantity section is displayed differently based on the screen size 
and the print types selected-->
<ng-template #printQtySection>
  <ion-item lines="none" class="grid-ion-item" >
    <!-- Full Sheet Print Quantity Input Field, shown if showFullSheetPrintQty is true -->
    <app-ion-input
      *ngIf="showFullSheetPrintQty"
      [templateFormGroup]="logForm"
      [labelPlacement]="labelPlacement"
      [formField]="logReceivingFormInputs['printQtySheet']"
      formControlName="printQtySheet"
      [disabledCondition]="!printingChecked"
      className="print-qty"
    >
    </app-ion-input>
    <!-- Dymo Print Quantity Input Field, shown if showDymoPrintQty is true -->
    <app-ion-input
      *ngIf="showDymoPrintQty"
      [templateFormGroup]="logForm"
      [labelPlacement]="labelPlacement"
      [formField]="logReceivingFormInputs['printQtyDymo']"
      formControlName="printQtyDymo"
      [disabledCondition]="!printingChecked"
      className="print-qty"
    >
    </app-ion-input>

    <!-- Print Button — opens the print-config modal (type / qty / method /
         printer) rather than printing immediately. -->
    <ion-button
      slot="end"
      id="print"
      (click)="openPrintConfig()"
      title="Print Label"
      [disabled]="!printingChecked || printTypeControl.value === null"
    >
      <ion-icon name="print" slot="icon-only"></ion-icon
    ></ion-button>
  </ion-item>
</ng-template>

<!--Log Form -->
<ng-template #appform>
  <ion-content>
    <form [formGroup]="logForm" class="log-form">
      <!--Form is displayed via ion grid. Each row is given a total size of '12', and the size of each column is specified-->
      <ion-grid class="log-form-grid">
        <!-- First Row: PO Number Input and Conditional Button -->
        <ion-row class="log-form-row">
          <ion-col size="6">
            <!-- PO Number input field - Placeholder and type are dynamic based on form config data -->
            <ion-item lines="none" class="grid-ion-item">
              <ion-input
                #inputElement
                formControlName="poNumber"
                [placeholder]="poFormField.placeholder"
                [type]="poValueHasLetters ? 'text' : 'number'"
                [labelPlacement]="labelPlacement"
                [label]="
                  poFormField.label +
                  (poFormField.required && !inputElement.value ? ' *' : '')
                "
                [errorText]="poFormField.errorMsg"
                (click)="onLogFormReset()"
                (ionFocus)="onLogFormReset()"
                (keyup.enter)="onSubmitPo()"
              >
              </ion-input>

              <!-- PO Settings Button-->
              <ion-button
                title="Toggle PO Number Input Type"
                slot="end"
                (click)="showPoTypeAlert()"
              >
                +ABC
              </ion-button>
            </ion-item>
          </ion-col>

          <ion-col size="6">
            <!-- PO Button appears only if a PO value has been entered -->
            <ion-item lines="none" class="grid-ion-item">
              <ng-template
                slot="end"
                *ngIf="poValueEntered"
                [ngTemplateOutlet]="logFormPOButton"
              ></ng-template>
            </ion-item>
          </ion-col>
        </ion-row>

        <!-- Second Row: Job Name and Job Number Inputs -->
        <ion-row class="log-form-row">
          <!--Job Name Input-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <ion-input
                #jobName
                formControlName="jobName"
                [placeholder]="logReceivingFormInputs['jobName'].placeholder"
                [type]="
                  logReceivingFormInputs['jobName'].type
                    ? logReceivingFormInputs['jobName'].type
                    : 'text'
                "
                [label]="
                  logReceivingFormInputs['jobName'].label +
                  (logReceivingFormInputs['jobName'].required && !jobName.value
                    ? ' *'
                    : '')
                "
                [labelPlacement]="labelPlacement"
              >
              </ion-input>
            </ion-item>
          </ion-col>
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <!-- Custom input component for job number -->
              <app-ion-input
                [templateFormGroup]="logForm"
                [labelPlacement]="labelPlacement"
                [formField]="logReceivingFormInputs['jobNumber']"
                formControlName="jobNumber"
              >
              </app-ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <!-- Third Rows: Vendor and Quantity Units -->
        <ion-row class="log-form-row">
          <!--Vendor Input-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <ion-input
                #vendor
                formControlName="vendor"
                [placeholder]="logReceivingFormInputs['vendor'].placeholder"
                [type]="
                  logReceivingFormInputs['vendor'].type
                    ? logReceivingFormInputs['vendor'].type
                    : 'text'
                "
                [label]="
                  logReceivingFormInputs['vendor'].label +
                  (logReceivingFormInputs['vendor'].required && !vendor.value
                    ? ' *'
                    : '')
                "
                [labelPlacement]="labelPlacement"
              >
              </ion-input>
            </ion-item>
          </ion-col>
          <!--Quantity Units Input-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <!-- Custom input component for quantity units -->
              <app-ion-input
                [templateFormGroup]="logForm"
                [labelPlacement]="labelPlacement"
                [formField]="logReceivingFormInputs['qtyUnits']"
                formControlName="qtyUnits"
              >
              </app-ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <!-- Fourth Row: Job Type and JC Department Inputs -->
        <ion-row class="log-form-row">
          <!--Job Type Input. Changing the job type alters the inputs displayed on the form-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <ion-select
                #jobType
                style="width: 100%"
                class="type-select"
                formControlName="type"
                [placeholder]="
                  logReceivingFormInputs['type'].placeholder
                    ? logReceivingFormInputs['type'].placeholder
                    : 'Enter ' +
                      logReceivingFormInputs['type'].label.toLowerCase()
                "
                aria-label="type"
                interface="popover"
                [labelPlacement]="labelPlacement"
                [label]="
                  logReceivingFormInputs['type'].label +
                  (logReceivingFormInputs['type'].required && !jobType.value
                    ? ' *'
                    : '')
                "
                (ionChange)="onJobTypeChange($event)"
              >
                <ion-select-option
                  *ngFor="let option of logReceivingFormInputs['type'].options"
                  [value]="option.label"
                  >{{ option.label }}</ion-select-option
                >
              </ion-select>
            </ion-item>
          </ion-col>

          <!--JC Department Input-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <!-- Custom input component for JC Department -->
              <app-ion-input
                [templateFormGroup]="logForm"
                [labelPlacement]="labelPlacement"
                [formField]="logReceivingFormInputs['jcDept']"
                formControlName="jcDept"
              >
              </app-ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <!-- Fifth Row: Shelf Location with Help button, and conditionally rendered Shop Sq Ft Input -->
        <ion-row class="log-form-row">
          <!--Shelf Location input followed by Help button-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item compact-item">
              <!-- Custom input component for shelf location -->
              <app-ion-input
                style="margin-right: 0px; padding-right: 0px"
                slot="start"
                [templateFormGroup]="logForm"
                [labelPlacement]="labelPlacement"
                [formField]="logReceivingFormInputs['shelfLocation']"
                formControlName="shelfLocation"
              >
              </app-ion-input>
              <!--Help Button. Clicking this will trigger the show the modal for Location Information--->
              <ion-button
                slot="start"
                id="view-location-info"
                (click)="onShowLocationInfo()"
                fill="clear"
                ><ion-icon name="help" slot="icon-only"></ion-icon
              ></ion-button>
            </ion-item>
          </ion-col>

          <!--Shop Sq Ft Input:
        Only rendered if the selected projectType is "Project"-->
          <ion-col size="6" *ngIf="isProject">
            <ion-item lines="none" class="grid-ion-item">
              <!-- Custom input component for shop sq ft -->
              <app-ion-input
                [templateFormGroup]="logForm"
                [labelPlacement]="labelPlacement"
                [formField]="logReceivingFormInputs['shopSqFt']"
                formControlName="shopSqFt"
              >
              </app-ion-input>
            </ion-item>
          </ion-col>
        </ion-row>

        <!--  Sixth Row: Notify Field with Add Contact and Email Buttons -->
        <ion-row class="log-form-row">
          <ion-col size="12">
            <!-- Notify Input-->
            <ion-item lines="none" class="grid-ion-item">
              <ion-textarea
                #notify
                formControlName="notify"
                [placeholder]="
                  logReceivingFormInputs['notify'].placeholder
                    ? logReceivingFormInputs['notify'].placeholder
                    : 'Enter ' +
                      logReceivingFormInputs['notify'].label.toLowerCase()
                "
                [type]="
                  logReceivingFormInputs['notify'].type
                    ? logReceivingFormInputs['notify'].type
                    : 'text'
                "
                [labelPlacement]="labelPlacement"
                [label]="
                  logReceivingFormInputs['notify'].label +
                  (logReceivingFormInputs['notify'].required && notify.value
                    ? ' *'
                    : '')
                "
                [readonly]="true"
              >
              </ion-textarea>
              <!--Edit Contacts Button. Clicking this will open the Add Contacts Modal-->
              <ion-button
                id="add-contact"
                (click)="onAddContact()"
                slot="end"
                title="Edit Contacts"
              >
                <ion-icon name="person-add" slot="icon-only"></ion-icon>
              </ion-button>
              <!--Open Email button. Clicking this will generate an email popup-->
              <ion-button
                id="email"
                (click)="onGenerateEmailOnly()"
                slot="end"
                title="Open New Email"
              >
                <ion-icon name="mail" slot="icon-only"></ion-icon>
              </ion-button>
            </ion-item>
          </ion-col>
        </ion-row>

        <!--Seventh Row: Items and Notes Inputs-->
        <ion-row class="log-form-row">
          <!--Items Input-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <ion-textarea
                #items
                formControlName="items"
                [placeholder]="
                  logReceivingFormInputs['items'].placeholder
                    ? logReceivingFormInputs['items'].placeholder
                    : 'Enter ' +
                      logReceivingFormInputs['items'].label.toLowerCase()
                "
                [type]="
                  logReceivingFormInputs['items'].type
                    ? logReceivingFormInputs['items'].type
                    : 'text'
                "
                [labelPlacement]="labelPlacement"
                [label]="
                  logReceivingFormInputs['items'].label +
                  (logReceivingFormInputs['items'].required && !items.value
                    ? ' *'
                    : '')
                "
              >
              </ion-textarea>

              <ion-text
                color="danger"
                *ngIf="logReceivingFormInputs['items'].errorMsg"
                >{{ logReceivingFormInputs["items"].errorMsg }}</ion-text
              >
            </ion-item>
          </ion-col>

          <!--Notes Input-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <ion-textarea
                #notes
                formControlName="notes"
                [placeholder]="
                  logReceivingFormInputs['notes'].placeholder
                    ? logReceivingFormInputs['notes'].placeholder
                    : 'Enter ' +
                      logReceivingFormInputs['notes'].label.toLowerCase()
                "
                [type]="
                  logReceivingFormInputs['notes'].type
                    ? logReceivingFormInputs['notes'].type
                    : 'text'
                "
                [labelPlacement]="labelPlacement"
                [label]="
                  logReceivingFormInputs['notes'].label +
                  (logReceivingFormInputs['notes'].required && !notes.value
                    ? ' *'
                    : '')
                "
              >
              </ion-textarea>
              <ion-text
                color="danger"
                *ngIf="logReceivingFormInputs['notes'].errorMsg"
                >{{ logReceivingFormInputs["notes"].errorMsg }}</ion-text
              >
            </ion-item>
          </ion-col>
        </ion-row>

        <!--Eighth Row: Print Label Checkbox, Print Type Select, Print Quantity Inputs-->
        <ion-row class="log-form-row">
          <!--Print Label Checkbox and Print Type Select-->
          <ion-col size="6">
            <ion-item lines="none" class="grid-ion-item">
              <ion-checkbox
                formControlName="printLabel"
                (ionChange)="onPrintCheckboxChange($event)"
                slot="start"
              >
              </ion-checkbox>

              <ion-select
                [ngClass]="{ 'large-screen-input': isLargeScreen }"
                formControlName="printType"
                [placeholder]="
                  logReceivingFormInputs['printType'].placeholder
                    ? logReceivingFormInputs['printType'].placeholder
                    : 'Enter ' +
                      logReceivingFormInputs['printType'].label.toLowerCase()
                "
                aria-label="printType"
                interface="popover"
                [labelPlacement]="labelPlacement"
                label="Print Label"
                slot="start"
                class="print-select"
                [disabled]="!printingChecked"
              >
                <ion-select-option
                  *ngFor="
                    let option of logReceivingFormInputs['printType'].options
                  "
                  [value]="option.label"
                >
                  {{ option.label }}
                </ion-select-option>
              </ion-select>

              <!-- Compact "print via" default; the full method/printer config
                   lives in the print-settings modal opened from the Print button. -->
              <ion-text
                *ngIf="printingChecked"
                slot="end"
                class="print-method-hint"
                color="medium"
              >
                <small>via Browser</small>
              </ion-text>
            </ion-item>
          </ion-col>

          <!--If the screen is not small, or if the screen is small but only one print option is selected, 
        then render the printQtySection template on the second half of the row-->
          <ion-col
            size="6"
            *ngIf="!isSmallScreen || (isSmallScreen && !showBothPrintQty)"
          >
            <ng-template [ngTemplateOutlet]="printQtySection"></ng-template>
          </ion-col>
        </ion-row>

        <!--If the screen is small and both print options are selected, 
        then render the printQtySection template on a new row (ninth row)-->
        <ion-row class="log-form-row" *ngIf="isSmallScreen && showBothPrintQty">
          <ion-col size="12">
            <ng-template [ngTemplateOutlet]="printQtySection"></ng-template>
          </ion-col>
        </ion-row>

        <!--9th Row: Photo Manager Button-->
        <ion-row class="log-form-row">
          <ion-col size="12">
            <ion-item lines="none" class="grid-ion-item">
              <ion-text *ngIf="photosDisabled" color="primary">
                <i>Photos Disabled </i></ion-text
              >
              <ion-text
                *ngIf="!photosDisabled"
                [color]="logEntryPhotos.length >= 3 ? 'medium' : 'warning'"
              >
                <i>{{ logEntryPhotos.length }} Photo(s) Uploaded </i></ion-text
              >

              <!--Photo Manager Button-->
              <ion-button
                (click)="onShowPhotoManager()"
                slot="end"
                title="Open Photo Manager"
                ><ion-icon name="camera" slot="icon-only"></ion-icon
              ></ion-button>
            </ion-item>
          </ion-col>
        </ion-row>

        <!--Last Row: Submit Button-->
        <div class="log-form-row">
          <ion-button
            expand="full"
            (click)="onReviewLog()"
            color="lightBlue"
            class="submit-btn"
            ><ion-icon name="checkmark"></ion-icon> &nbsp;Review & Submit
            Entry</ion-button
          >
        </div>
      </ion-grid>
    </form>
  </ion-content>
</ng-template>
